import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bell, Plus, RefreshCw, Trash2, Pause, Play, ExternalLink } from "lucide-react";
import {
  listMonitors, listMonitorEvents, createMonitor,
  deleteMonitor, setMonitorEnabled, runMonitorNow,
} from "@/lib/intel/monitor.functions";
import { detectTargetType } from "@/lib/intel/detect";

const SIGNALS = [
  { key: "breach", label: "New breach" },
  { key: "company_role", label: "Company role / filing" },
  { key: "sanctions", label: "Sanctions" },
  { key: "lawsuit", label: "Lawsuit" },
  { key: "new_domain", label: "New domain / cert" },
  { key: "github_leak", label: "GitHub leak" },
  { key: "media", label: "Media mention" },
] as const;

export function WatchlistPanel() {
  const qc = useQueryClient();
  const listFn = useServerFn(listMonitors);
  const eventsFn = useServerFn(listMonitorEvents);
  const createFn = useServerFn(createMonitor);
  const deleteFn = useServerFn(deleteMonitor);
  const toggleFn = useServerFn(setMonitorEnabled);
  const runFn = useServerFn(runMonitorNow);

  const monitors = useQuery({ queryKey: ["monitors"], queryFn: () => listFn(), staleTime: 30_000 });
  const events = useQuery({ queryKey: ["monitor-events"], queryFn: () => eventsFn(), staleTime: 30_000 });

  const [target, setTarget] = useState("");
  const [signals, setSignals] = useState<string[]>([]);
  const [cadence, setCadence] = useState(24);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["monitors"] });
    qc.invalidateQueries({ queryKey: ["monitor-events"] });
  };

  const create = useMutation({
    mutationFn: () =>
      createFn({ data: { target: target.trim(), signals: signals as any, cadenceHours: cadence } }),
    onSuccess: () => { setTarget(""); setSignals([]); toast.success("Target added to live monitoring"); invalidate(); },
    onError: (e: any) => toast.error(e?.message ?? "Could not add monitor"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => { toast.success("Monitor removed"); invalidate(); },
    onError: (e: any) => toast.error(e?.message ?? "Could not remove monitor"),
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) => toggleFn({ data: v }),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.message ?? "Could not update monitor"),
  });
  const runNow = useMutation({
    mutationFn: (id: string) => runFn({ data: { id } }),
    onSuccess: (r: any) => { toast.success(r?.newEvents ? `${r.newEvents} new change(s) detected` : "No changes since last check"); invalidate(); },
    onError: (e: any) => toast.error(e?.message ?? "Check failed"),
  });

  const rows = monitors.data?.monitors ?? [];
  const cap = monitors.data?.cap ?? 0;
  const detected = target.trim() ? detectTargetType(target.trim()) : null;

  return (
    <div className="space-y-3">
      <div className="glass rounded-xl p-3">
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 pb-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
            Watchlist · continuous monitoring
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">
            {rows.length}/{cap || "—"} targets · re-checked every {cadence}h
          </div>
        </div>

        <div className="grid gap-2 rounded-md border border-panel-border bg-background/40 p-3">
          <div className="flex flex-col gap-2 md:flex-row">
            <input
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && target.trim()) create.mutate(); }}
              placeholder="Email, company, domain, wallet, phone, person or username…"
              className="flex-1 rounded-md border border-panel-border bg-background/60 px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
            />
            <select
              value={cadence}
              onChange={(e) => setCadence(Number(e.target.value))}
              className="rounded-md border border-panel-border bg-background/60 px-2 py-2 text-sm"
            >
              <option value={12}>Every 12h</option>
              <option value={24}>Daily</option>
              <option value={72}>Every 3 days</option>
              <option value={168}>Weekly</option>
            </select>
            <button
              onClick={() => create.mutate()}
              disabled={!target.trim() || create.isPending}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground ring-glow disabled:opacity-50"
            >
              <Plus className="h-4 w-4" /> Monitor
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {detected && (
              <span className="rounded-sm border border-cyan/40 bg-cyan/10 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-cyan">
                {detected}
              </span>
            )}
            {SIGNALS.map((s) => {
              const on = signals.includes(s.key);
              return (
                <button
                  key={s.key}
                  onClick={() => setSignals((v) => (on ? v.filter((x) => x !== s.key) : [...v, s.key]))}
                  className={`rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                    on ? "border-cyan/60 bg-cyan/10 text-cyan" : "border-panel-border text-muted-foreground"
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
            <span className="text-[10px] text-muted-foreground">
              {signals.length ? "" : "no filter = all signals relevant to the target type"}
            </span>
          </div>
        </div>

        <div className="mt-3 divide-y divide-panel-border overflow-hidden rounded-md border border-panel-border">
          {monitors.isLoading && <div className="p-4 text-center text-sm text-muted-foreground">Loading watchlist…</div>}
          {!monitors.isLoading && rows.length === 0 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No monitored targets yet. Add an email, company, domain, wallet, phone, person or username above.
            </div>
          )}
          {rows.map((m) => (
            <div key={m.id} className="grid gap-2 px-3 py-2 text-xs md:grid-cols-12 md:items-center">
              <div className="md:col-span-4">
                <div className="truncate font-medium">{m.target}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {m.targetType} · every {m.cadenceHours}h
                </div>
              </div>
              <div className="truncate text-muted-foreground md:col-span-4">
                {(m.signals.length ? m.signals : ["auto"]).join(" · ")}
              </div>
              <div className="font-mono text-[10px] text-muted-foreground md:col-span-2">
                {m.lastCheckedAt ? `checked ${new Date(m.lastCheckedAt).toLocaleString()}` : "baseline pending"}
              </div>
              <div className="flex items-center justify-end gap-1 md:col-span-2">
                <button title="Check now" onClick={() => runNow.mutate(m.id)} className="rounded p-1.5 hover:bg-panel">
                  <RefreshCw className={`h-3.5 w-3.5 ${runNow.isPending ? "animate-spin" : ""}`} />
                </button>
                <button
                  title={m.enabled ? "Pause" : "Resume"}
                  onClick={() => toggle.mutate({ id: m.id, enabled: !m.enabled })}
                  className="rounded p-1.5 hover:bg-panel"
                >
                  {m.enabled ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 text-amber" />}
                </button>
                <button title="Remove" onClick={() => remove.mutate(m.id)} className="rounded p-1.5 hover:bg-panel">
                  <Trash2 className="h-3.5 w-3.5 text-danger" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="glass rounded-xl p-3">
        <div className="flex items-center gap-2 px-1 pb-2">
          <Bell className="h-3.5 w-3.5 text-cyan" />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Detected changes</div>
        </div>
        <div className="divide-y divide-panel-border overflow-hidden rounded-md border border-panel-border">
          {(events.data ?? []).length === 0 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No changes detected yet. The first check stores a baseline; you are alerted only on new activity.
            </div>
          )}
          {(events.data ?? []).map((e) => (
            <div key={e.id} className="grid gap-1 px-3 py-2 text-xs md:grid-cols-12">
              <div className="md:col-span-2">
                <span
                  className={`rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                    e.severity === "critical"
                      ? "border-danger/50 bg-danger/10 text-danger"
                      : e.severity === "warning"
                      ? "border-amber/50 bg-amber/10 text-amber"
                      : "border-panel-border text-muted-foreground"
                  }`}
                >
                  {e.signal}
                </span>
              </div>
              <div className="md:col-span-6">
                <div className="truncate">{e.title}</div>
                {e.detail && <div className="truncate text-[11px] text-muted-foreground">{e.detail}</div>}
              </div>
              <div className="truncate font-mono text-[10px] text-muted-foreground md:col-span-2">{e.target}</div>
              <div className="flex items-center justify-end gap-2 font-mono text-[10px] text-muted-foreground md:col-span-2">
                {new Date(e.createdAt).toLocaleString()}
                {e.ref && (
                  <a href={e.ref} target="_blank" rel="noopener noreferrer" className="text-cyan">
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}